<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useToggle } from '@vueuse/core';

import VaultSageAPI from 'dashboard/api/vaultsage';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AccessToken from 'dashboard/routes/dashboard/settings/profile/AccessToken.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  selectedBot: {
    type: Object,
    default: () => ({}),
  },
});

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { t } = useI18n();
const dialogRef = ref(null);
const uiFlags = useMapGetter('agentBots/getUIFlags');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const currentAccountId = useMapGetter('getCurrentAccountId');
const vaultSageIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'vaultsage'
);

const formState = reactive({
  botName: '',
  botDescription: '',
  botUrl: '',
  botAvatar: null,
  botAvatarUrl: '',
  botType: 'webhook',
  smartdropLinksInput: '',
  selectedFileIds: [],
  selectedFileNames: [],
});

const [showAccessToken, toggleAccessToken] = useToggle();
const accessToken = ref('');
const submitted = ref(false);
const vaultSageFiles = ref([]);
const vaultSageSearchQuery = ref('');
const isLoadingVaultSageFiles = ref(false);
const resolvedSmartDropFiles = ref([]);
const isResolvingSmartDropLinks = ref(false);

const isLoading = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? uiFlags.value.isCreating
    : uiFlags.value.isUpdating
);

const isSageIntegrationEnabled = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.SAGE)
);

const providerOptions = computed(() => {
  const options = [
    {
      value: 'webhook',
      label: t('AGENT_BOTS.TYPES.WEBHOOK'),
    },
  ];
  if (isSageIntegrationEnabled.value) {
    options.push({
      value: 'vaultsage',
      label: t('AGENT_BOTS.TYPES.VAULTSAGE'),
    });
  }
  return options;
});

const isVaultSageBot = computed(() => formState.botType === 'vaultsage');
const isVaultSageConnected = computed(
  () => !!vaultSageIntegration.value?.hooks?.length
);

const dialogTitle = computed(() => {
  if (showAccessToken.value) {
    return t('AGENT_BOTS.ACCESS_TOKEN.TITLE');
  }

  return props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.ADD.TITLE')
    : t('AGENT_BOTS.EDIT.TITLE');
});

const dialogDescription = computed(() => {
  if (showAccessToken.value) {
    return t('AGENT_BOTS.ACCESS_TOKEN.DESCRIPTION');
  }
  return '';
});

const confirmButtonLabel = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.FORM.CREATE')
    : t('AGENT_BOTS.FORM.UPDATE')
);

const showAccessTokenInput = computed(
  () =>
    showAccessToken.value ||
    props.type === MODAL_TYPES.EDIT ||
    accessToken.value
);

const smartdropLinks = computed(() =>
  formState.smartdropLinksInput
    .split('\n')
    .map(link => link.trim())
    .filter(Boolean)
);

const selectedVaultSageFiles = computed(() =>
  formState.selectedFileIds.map((value, index) => ({
    value,
    label: formState.selectedFileNames[index] || value,
  }))
);

const isWebhookBot = computed(() => formState.botType === 'webhook');

const botNameError = computed(() => {
  if (!submitted.value || formState.botName.trim()) return '';
  return t('AGENT_BOTS.FORM.ERRORS.NAME');
});

const botUrlError = computed(() => {
  if (!isWebhookBot.value) return '';
  if (!submitted.value) return '';
  if (!formState.botUrl.trim()) return t('AGENT_BOTS.FORM.ERRORS.URL');

  try {
    const parsedUrl = new URL(formState.botUrl);
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return t('AGENT_BOTS.FORM.ERRORS.VALID_URL');
    }
  } catch {
    return t('AGENT_BOTS.FORM.ERRORS.VALID_URL');
  }

  return '';
});

const vaultSageError = computed(() => {
  if (!submitted.value || !isVaultSageBot.value) return '';
  if (!isVaultSageConnected.value) {
    return t('AGENT_BOTS.VAULTSAGE.CONNECT_REQUIRED');
  }
  if (!formState.selectedFileIds.length && !smartdropLinks.value.length) {
    return t('AGENT_BOTS.VAULTSAGE.FILE_REQUIRED');
  }
  return '';
});

const resetForm = () => {
  Object.assign(formState, {
    botName: '',
    botDescription: '',
    botUrl: '',
    botAvatar: null,
    botAvatarUrl: '',
    botType: 'webhook',
    smartdropLinksInput: '',
    selectedFileIds: [],
    selectedFileNames: [],
  });
  vaultSageFiles.value = [];
  vaultSageSearchQuery.value = '';
  resolvedSmartDropFiles.value = [];
  submitted.value = false;
};

const handleImageUpload = ({ file, url: avatarUrl }) => {
  formState.botAvatar = file;
  formState.botAvatarUrl = avatarUrl;
};

const handleAvatarDelete = async () => {
  if (props.selectedBot?.id) {
    try {
      await store.dispatch(
        'agentBots/deleteAgentBotAvatar',
        props.selectedBot.id
      );
      formState.botAvatar = null;
      formState.botAvatarUrl = '';
      useAlert(t('AGENT_BOTS.AVATAR.SUCCESS_DELETE'));
    } catch (error) {
      useAlert(t('AGENT_BOTS.AVATAR.ERROR_DELETE'));
    }
  } else {
    formState.botAvatar = null;
    formState.botAvatarUrl = '';
  }
};

const validateForm = () => {
  return !botNameError.value && !botUrlError.value && !vaultSageError.value;
};

const buildVaultSageBotConfig = () => ({
  selected_file_ids: formState.selectedFileIds,
  selected_file_names: formState.selectedFileNames,
  smartdrop_links: smartdropLinks.value,
});

const handleSubmit = async () => {
  submitted.value = true;
  if (!validateForm() || showAccessToken.value) return;

  const botData = {
    name: formState.botName,
    description: formState.botDescription,
    bot_type: formState.botType,
    avatar: formState.botAvatar,
  };

  if (isWebhookBot.value) {
    botData.outgoing_url = formState.botUrl;
  } else {
    botData.bot_config = buildVaultSageBotConfig();
  }

  const isCreate = props.type === MODAL_TYPES.CREATE;

  try {
    const actionPayload = isCreate
      ? botData
      : { id: props.selectedBot.id, data: botData };

    const response = await store.dispatch(
      `agentBots/${isCreate ? 'create' : 'update'}`,
      actionPayload
    );

    useAlert(
      isCreate
        ? t('AGENT_BOTS.ADD.API.SUCCESS_MESSAGE')
        : t('AGENT_BOTS.EDIT.API.SUCCESS_MESSAGE')
    );

    if (isCreate) {
      const { access_token: responseAccessToken, id } = response || {};

      if (isWebhookBot.value && id && responseAccessToken) {
        accessToken.value = responseAccessToken;
        toggleAccessToken(true);
      } else {
        accessToken.value = '';
        dialogRef.value.close();
      }
    } else {
      dialogRef.value.close();
    }

    resetForm();
  } catch (error) {
    useAlert(
      error?.message ||
        (isCreate
          ? t('AGENT_BOTS.ADD.API.ERROR_MESSAGE')
          : t('AGENT_BOTS.EDIT.API.ERROR_MESSAGE'))
    );
  }
};

const initializeForm = () => {
  if (props.selectedBot && Object.keys(props.selectedBot).length) {
    const {
      name,
      description,
      outgoing_url: botUrl,
      thumbnail,
      bot_type: botType,
      bot_config: botConfig,
      access_token: botAccessToken,
    } = props.selectedBot;

    formState.botName = name || '';
    formState.botDescription = description || '';
    formState.botUrl = botUrl || '';
    formState.botAvatarUrl = thumbnail || '';
    formState.botType = botType || 'webhook';
    formState.smartdropLinksInput = Array(
      botConfig?.smartdrop_links || []
    ).join('\n');
    formState.selectedFileIds = Array(botConfig?.selected_file_ids || []);
    formState.selectedFileNames = Array(botConfig?.selected_file_names || []);
    resolvedSmartDropFiles.value = Array(
      botConfig?.resolved_file_names || []
    ).map((nameValue, index) => ({
      name: nameValue,
      id: botConfig?.resolved_file_ids?.[index],
    }));

    if (botAccessToken && props.type === MODAL_TYPES.EDIT) {
      accessToken.value = botAccessToken;
    }
  } else {
    resetForm();
  }
};

const onCopyToken = async value => {
  await copyTextToClipboard(value);
  useAlert(t('AGENT_BOTS.ACCESS_TOKEN.COPY_SUCCESSFUL'));
};

const onResetToken = async () => {
  const response = await store.dispatch(
    'agentBots/resetAccessToken',
    props.selectedBot.id
  );
  if (response) {
    accessToken.value = response.access_token;
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_SUCCESS'));
  } else {
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_ERROR'));
  }
};

const closeModal = () => {
  accessToken.value = '';
  toggleAccessToken(false);
  submitted.value = false;
};

const onClickClose = () => {
  closeModal();
  dialogRef.value.close();
};

const fetchVaultSageFiles = async () => {
  if (!isVaultSageConnected.value) return;

  isLoadingVaultSageFiles.value = true;
  try {
    const {
      data: { payload },
    } = await VaultSageAPI.listFiles({
      q: vaultSageSearchQuery.value || undefined,
    });
    vaultSageFiles.value = payload || [];
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('AGENT_BOTS.VAULTSAGE.FETCH_ERROR')
    );
  } finally {
    isLoadingVaultSageFiles.value = false;
  }
};

const toggleVaultSageFile = file => {
  const currentIndex = formState.selectedFileIds.indexOf(file.id);

  if (currentIndex === -1) {
    formState.selectedFileIds.push(file.id);
    formState.selectedFileNames.push(file.name);
  } else {
    formState.selectedFileIds.splice(currentIndex, 1);
    formState.selectedFileNames.splice(currentIndex, 1);
  }
};

const resolveSmartDropLinks = async () => {
  if (!smartdropLinks.value.length) {
    resolvedSmartDropFiles.value = [];
    return;
  }

  isResolvingSmartDropLinks.value = true;
  try {
    const {
      data: { payload },
    } = await VaultSageAPI.resolveShareLinks(smartdropLinks.value);
    resolvedSmartDropFiles.value = payload || [];
    useAlert(
      t('AGENT_BOTS.VAULTSAGE.RESOLVE_SUCCESS', {
        count: resolvedSmartDropFiles.value.length,
      })
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('AGENT_BOTS.VAULTSAGE.RESOLVE_ERROR')
    );
  } finally {
    isResolvingSmartDropLinks.value = false;
  }
};

watch(() => props.selectedBot, initializeForm, { immediate: true, deep: true });

watch(
  isVaultSageBot,
  value => {
    if (value && isVaultSageConnected.value && !vaultSageFiles.value.length) {
      fetchVaultSageFiles();
    }
  },
  { immediate: true }
);

watch(isSageIntegrationEnabled, enabled => {
  if (!enabled && formState.botType === 'vaultsage') {
    formState.botType = 'webhook';
  }
});

onMounted(() => {
  store.dispatch('integrations/get');
});

const goToInboxList = () => {
  router.push(accountScopedRoute('settings_inbox_list'));
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="dialogTitle"
    :description="dialogDescription"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    position="top"
    width="2xl"
    @close="closeModal"
  >
    <form
      class="flex max-h-[calc(100vh-8rem)] flex-col gap-4 overflow-y-auto pr-1"
      @submit.prevent="handleSubmit"
    >
      <div
        v-if="!showAccessToken || type === MODAL_TYPES.EDIT"
        class="flex flex-col gap-4"
      >
        <div class="mb-2 flex flex-col items-start">
          <span class="mb-2 text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_BOTS.FORM.AVATAR.LABEL') }}
          </span>
          <Avatar
            :src="formState.botAvatarUrl"
            :name="formState.botName"
            :size="68"
            allow-upload
            icon-name="i-lucide-bot-message-square"
            @upload="handleImageUpload"
            @delete="handleAvatarDelete"
          />
        </div>

        <Input
          id="bot-name"
          v-model="formState.botName"
          :label="$t('AGENT_BOTS.FORM.NAME.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.NAME.PLACEHOLDER')"
          :message="botNameError"
          :message-type="botNameError ? 'error' : 'info'"
        />

        <TextArea
          id="bot-description"
          v-model="formState.botDescription"
          :label="$t('AGENT_BOTS.FORM.DESCRIPTION.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.DESCRIPTION.PLACEHOLDER')"
        />

        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_BOTS.FORM.PROVIDER.LABEL') }}
          </span>
          <SelectInput v-model="formState.botType" :options="providerOptions" />
        </div>

        <Input
          v-if="isWebhookBot"
          id="bot-url"
          v-model="formState.botUrl"
          :label="$t('AGENT_BOTS.FORM.WEBHOOK_URL.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.WEBHOOK_URL.PLACEHOLDER')"
          :message="botUrlError"
          :message-type="botUrlError ? 'error' : 'info'"
        />

        <div
          v-if="isVaultSageBot"
          class="rounded-xl border border-n-weak bg-n-alpha-2 p-4 flex flex-col gap-4"
        >
          <div class="flex flex-col gap-1">
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ $t('AGENT_BOTS.VAULTSAGE.TITLE') }}
            </h4>
            <p class="text-sm text-n-slate-11">
              {{
                isVaultSageConnected
                  ? $t('AGENT_BOTS.VAULTSAGE.CONNECTED_HINT')
                  : $t('AGENT_BOTS.VAULTSAGE.CONNECT_REQUIRED')
              }}
            </p>
          </div>

          <TextArea
            v-model="formState.smartdropLinksInput"
            :label="$t('AGENT_BOTS.VAULTSAGE.SMARTDROP_LABEL')"
            :placeholder="$t('AGENT_BOTS.VAULTSAGE.SMARTDROP_PLACEHOLDER')"
          />

          <div class="flex justify-end">
            <NextButton
              type="button"
              faded
              slate
              :disabled="!smartdropLinks.length"
              :is-loading="isResolvingSmartDropLinks"
              :label="$t('AGENT_BOTS.VAULTSAGE.RESOLVE_BUTTON')"
              @click="resolveSmartDropLinks"
            />
          </div>

          <div
            v-if="resolvedSmartDropFiles.length"
            class="rounded-lg bg-n-solid-1 p-3 text-sm text-n-slate-11"
          >
            <p class="mb-2 text-n-slate-12 font-medium">
              {{ $t('AGENT_BOTS.VAULTSAGE.RESOLVED_FILES') }}
            </p>
            <ul class="list-disc pl-4 m-0">
              <li
                v-for="file in resolvedSmartDropFiles"
                :key="file.id || file.name"
                class="break-all"
              >
                {{ file.name || file.id }}
              </li>
            </ul>
          </div>

          <div
            v-if="isVaultSageConnected"
            class="flex flex-col gap-1 rounded-lg bg-n-solid-1 p-3"
          >
            <p class="text-sm text-n-slate-11 m-0">
              {{ $t('AGENT_BOTS.VAULTSAGE.ATTACH_NEXT_STEP') }}
            </p>
            <button
              type="button"
              class="text-sm font-medium text-n-blue-11 hover:underline text-start p-0 border-0 bg-transparent cursor-pointer"
              @click="goToInboxList"
            >
              {{ $t('AGENT_BOTS.VAULTSAGE.OPEN_INBOXES_LINK') }}
            </button>
          </div>

          <div class="flex flex-col gap-3">
            <div class="flex gap-2 items-end">
              <div class="flex-1">
                <Input
                  v-model="vaultSageSearchQuery"
                  :label="$t('AGENT_BOTS.VAULTSAGE.FILE_SEARCH_LABEL')"
                  :placeholder="
                    $t('AGENT_BOTS.VAULTSAGE.FILE_SEARCH_PLACEHOLDER')
                  "
                />
              </div>
              <NextButton
                type="button"
                :label="$t('AGENT_BOTS.VAULTSAGE.SEARCH_BUTTON')"
                :is-loading="isLoadingVaultSageFiles"
                :disabled="!isVaultSageConnected"
                @click="fetchVaultSageFiles"
              />
            </div>

            <div
              v-if="vaultSageFiles.length"
              class="max-h-48 overflow-auto rounded-lg border border-n-weak"
            >
              <button
                v-for="file in vaultSageFiles"
                :key="file.id"
                type="button"
                class="w-full flex items-center justify-between px-3 py-2 text-left border-b border-n-weak last:border-b-0 hover:bg-n-alpha-2"
                @click="toggleVaultSageFile(file)"
              >
                <span class="text-sm text-n-slate-12">{{ file.name }}</span>
                <span
                  class="text-xs"
                  :class="
                    formState.selectedFileIds.includes(file.id)
                      ? 'text-n-blue-11'
                      : 'text-n-slate-10'
                  "
                >
                  {{
                    formState.selectedFileIds.includes(file.id)
                      ? $t('AGENT_BOTS.VAULTSAGE.SELECTED')
                      : $t('AGENT_BOTS.VAULTSAGE.SELECT')
                  }}
                </span>
              </button>
            </div>

            <div
              v-if="selectedVaultSageFiles.length"
              class="rounded-lg bg-n-solid-1 p-3 text-sm text-n-slate-11"
            >
              <p class="mb-2 text-n-slate-12 font-medium">
                {{ $t('AGENT_BOTS.VAULTSAGE.SELECTED_FILES') }}
              </p>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="file in selectedVaultSageFiles"
                  :key="file.value"
                  class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-12"
                >
                  {{ file.label }}
                </span>
              </div>
            </div>
          </div>

          <p v-if="vaultSageError" class="text-xs text-n-ruby-9 mb-0">
            {{ vaultSageError }}
          </p>
        </div>
      </div>

      <div v-if="showAccessTokenInput" class="flex flex-col gap-1">
        <label
          v-if="type === MODAL_TYPES.EDIT"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ $t('AGENT_BOTS.ACCESS_TOKEN.TITLE') }}
        </label>
        <AccessToken
          v-if="type === MODAL_TYPES.EDIT"
          :value="accessToken"
          @on-copy="onCopyToken"
          @on-reset="onResetToken"
        />
        <AccessToken
          v-else
          :value="accessToken"
          :show-reset-button="false"
          @on-copy="onCopyToken"
        />
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('AGENT_BOTS.FORM.CANCEL')"
          @click="onClickClose()"
        />
        <NextButton
          v-if="!showAccessToken"
          type="submit"
          data-testid="label-submit"
          :label="confirmButtonLabel"
          :is-loading="isLoading"
        />
      </div>
    </form>
  </Dialog>
</template>
